import { useCallback } from "react";

import { getEnvironment, useGetFieldPracticeTimestamps } from "utils";

import { CropFCPO, PracticeFCPO } from "models";

import {
  getCropDataToConfirm,
  getCropSeedingReviewDate,
  isCropInReviewScope,
  isDataDueForReview,
  isPracticeDueForReview,
} from "pages/app/all-field-view/utils/AllFieldsUtils";

export type CropDataToReview = {
  seedingData: boolean;
  seedingType: boolean;
  harvestData: boolean;
};

export const useCropDataToReview = ({
  crop,
  fieldId,
  isFieldEnrolled,
  isSubmissionPeriodOpen,
}: {
  crop: CropFCPO;
  fieldId: string;
  isFieldEnrolled: boolean;
  isSubmissionPeriodOpen: boolean;
}) => {
  // only enrolled fields are submitted, so other fields have nothing to review
  const isReviewOpen = isFieldEnrolled && isSubmissionPeriodOpen;
  const isInReview = isReviewOpen && isCropInReviewScope(crop);
  // the timestamps come from a user route; the admin portal shows no review tags, so it never loads them
  const { data: updatedAtById } = useGetFieldPracticeTimestamps({ fieldId, enabled: isInReview && !getEnvironment().admin });

  // the crop is reviewed against the same fresh timestamps as its practices, or its own entry until they load
  const cropUpdatedAt = updatedAtById?.[crop.id] ?? crop.updatedAt;
  const { seedingData, harvestData } = getCropDataToConfirm({ ...crop, updatedAt: cropUpdatedAt }, isReviewOpen);
  const seedingUpdatedAt = crop.seedingPracticeId ? updatedAtById?.[crop.seedingPracticeId] : undefined;
  const seedingType = isInReview && !!crop.seedingPracticeType && !!seedingUpdatedAt && isDataDueForReview(getCropSeedingReviewDate(crop), seedingUpdatedAt);

  const isPracticeToReview = useCallback(
    (practice: PracticeFCPO) => isPracticeDueForReview(practice, crop, practice.id ? updatedAtById?.[practice.id] : undefined, isReviewOpen),
    [crop, updatedAtById, isReviewOpen]
  );

  const dataToReview: CropDataToReview = { seedingData, seedingType, harvestData };

  return { dataToReview, isPracticeToReview };
};
